<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { authFlow } from '@/integrations/Immersve/auth/AuthFlow';
import { transientState } from '@/integrations/Immersve/auth/TransientState';
import { LockClosedIcon } from '@heroicons/vue/24/outline';
import AuthScreenLayout from '@/layout/AuthScreenLayout.vue';
import { useAuthStore } from '@/stores/auth.store';

// The only way into the auth flow. Entering it automatically — from the 401
// interceptor, the route guard, or the Back button — would make signing out
// impossible, since the next navigation would silently sign the user back in.
// See decisions.md, 2026-09-10.
type LoginReason = 'signed-out' | 'expired';

type LoginMessage = { heading: string; detail?: string };

const MESSAGES: Record<LoginReason, LoginMessage> = {
  'signed-out': {
    heading: "You've been signed out",
    detail: 'Sign in again whenever you are ready.',
  },
  expired: {
    heading: 'Your session has expired',
    detail: 'Sign in again to pick up where you left off.',
  },
};

// No detail line: the heading and the button already say the whole of it.
const DEFAULT_MESSAGE: LoginMessage = {
  heading: 'Sign in to continue',
};

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const message = computed(() => {
  const reason = route.query.reason;
  return typeof reason === 'string' && reason in MESSAGES
    ? MESSAGES[reason as LoginReason]
    : DEFAULT_MESSAGE;
});

// Nothing to ask of someone who already has a session — prompting them to
// sign in would be a dead end they cannot leave. Waits for the probe rather
// than trusting the cached status: that status outlives the cookie, so a
// stale 'authenticated' would bounce the visitor onto a route the guard sends
// straight back, replacing whatever this screen was asked to say.
onMounted(async () => {
  await authStore.initialize();
  if (authStore.status === 'authenticated') {
    router.replace(transientState.takeReturnTo() || '/');
  }
});

function signIn() {
  authFlow.enter();
}
</script>

<template>
  <AuthScreenLayout>
    <div data-test="screen-login" class="flex flex-col items-center">
      <LockClosedIcon class="mb-3 size-10 text-gray-400" aria-hidden="true" />
      <h1 class="mb-6 text-3xl font-bold text-gray-900" data-test="login-heading">
        {{ message.heading }}
      </h1>
      <p v-if="message.detail" data-test="login-detail" class="-mt-2 mb-6 max-w-md text-gray-500">
        {{ message.detail }}
      </p>
      <button
        type="button"
        data-test="btn-sign-in"
        class="cursor-pointer rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-indigo-700"
        @click="signIn"
      >
        Sign in
      </button>
    </div>
  </AuthScreenLayout>
</template>
